import type { PrioritizationSession } from '../../../types/prioritization/session';
import { resolveRunMode } from '../../../types/prioritization/session';
import { resolveSessionToolDescriptor } from '../../../lib/sessionToolCatalog';

export type SessionGroup = 'needs_input' | 'live' | 'upcoming' | 'completed';
export type DashboardFilter = 'all' | 'needs_input' | 'live' | 'upcoming' | 'completed';

export function isSessionCompleted(s: PrioritizationSession): boolean {
  return s.state === 'RUN_COMPLETE' || s.state === 'PUBLISHED';
}

export function isSessionLive(s: PrioritizationSession): boolean {
  const runMode = resolveRunMode(s.modeConfig);
  return runMode !== 'open' && !isSessionCompleted(s) && s.state !== 'DRAFT';
}

export function isSessionUpcoming(s: PrioritizationSession): boolean {
  if (isSessionCompleted(s)) return false;
  if (s.scheduledFor && new Date(s.scheduledFor) > new Date()) return true;
  const opensAt = s.modeConfig.opensAt;
  if (opensAt && new Date(opensAt) > new Date()) return true;
  return false;
}

export function classifySession(s: PrioritizationSession): SessionGroup {
  if (isSessionCompleted(s)) return 'completed';
  if (isSessionUpcoming(s)) return 'upcoming';
  if (isSessionLive(s)) return 'live';
  return 'needs_input';
}

export function cardBorderColor(group: SessionGroup): string {
  switch (group) {
    case 'live': return '#C0533A';
    case 'needs_input': return '#D4A017';
    case 'upcoming': return '#3D4B6F';
    case 'completed': return '#3A7D56';
    default: return '#222D40';
  }
}

export function formatShortDate(d: Date | string | undefined): string {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

export function firstName(userName: string): string {
  return (userName || 'there').split(' ')[0];
}

export function intentLabel(s: PrioritizationSession): string {
  if (s.intent === 'rank_priorities' || s.decisionClass === 'S6_ITEM_PRIORITIZATION') {
    return 'Force Rank';
  }
  if (s.intent === 'converge_values' || s.decisionClass === 'S5_VALUE_SELECTION') {
    return 'Value Entry';
  }
  if (s.intent === 'select_attributes' || s.decisionClass === 'S3_ATTRIBUTE_SELECTION') {
    return 'Criteria';
  }
  if (s.intent === 'select_item_pool' || s.decisionClass === 'S2_ITEM_POOL_SELECTION') {
    return 'Pool Selection';
  }
  if (s.decisionClass === 'S1_PARTICIPATION_PRIORITIZATION') return 'Participation';
  if (s.intent === 'calibration') return 'Calibration';
  if (s.intent === 'review') return 'Review';
  if (s.selectedToolId) {
    return s.selectedToolId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  return 'Session';
}

/**
 * Whether the open-session room (ParticipantAsyncToolRoom) can show this
 * session's options. It reads workspace items (`itemIds`) and an Event
 * Designer event's items or criteria (`modeConfig.v3Config`). Options typed for
 * the session alone (`isolatedItems`, e.g. an offline blueprint's candidates)
 * are shown only by the product's live room, so such a session's people answer
 * there whatever its run mode — as they always have (the room choice used to
 * ride on a run mode the browser and the column disagreed about; migration 1045).
 */
export function asyncRoomCanShowOptions(s: PrioritizationSession): boolean {
  if ((s.itemIds ?? []).length > 0) return true;
  const v3 = (s.modeConfig as unknown as Record<string, unknown> | undefined)?.v3Config as
    | { items?: unknown[]; criteria?: unknown[]; primaryGoalStep?: string }
    | undefined;
  if (v3?.primaryGoalStep === 'S3' && (v3.criteria ?? []).length > 0) return true;
  return (v3?.items ?? []).length > 0;
}

export function isRankingSession(s: PrioritizationSession): boolean {
  return s.intent === 'rank_priorities'
    || s.decisionClass === 'S6_ITEM_PRIORITIZATION'
    || s.sessionType === 'arrange';
}

export function isValueSession(s: PrioritizationSession): boolean {
  return s.intent === 'converge_values' || s.decisionClass === 'S5_VALUE_SELECTION';
}

export function isConfigSession(s: PrioritizationSession): boolean {
  return !isRankingSession(s) && !isValueSession(s)
    && (s.intent === 'select_attributes'
      || s.intent === 'select_item_pool'
      || s.intent === 'calibration'
      || s.intent === 'review'
      || s.decisionClass === 'S1_PARTICIPATION_PRIORITIZATION'
      || s.decisionClass === 'S3_ATTRIBUTE_SELECTION'
      || s.decisionClass === 'S2_ITEM_POOL_SELECTION'
      || s.decisionClass === 'S4_VALUES_CHOICE_SELECTION');
}

export function toolDisplayName(s: PrioritizationSession): string {
  if (!s.selectedToolId) return 'Prioritization';
  // The method's own name from the catalog; the raw id read "Dot_voting" on the
  // participant's first screen.
  const named = resolveSessionToolDescriptor(s.selectedToolId)?.name;
  if (named) return named;
  return s.selectedToolId.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
